/**
 * mapConstants — Shared Constants & Utilities for CosmicMap
 * ──────────────────────────────────────────────────────────
 * Extracted so that ProjectNode, ChallengeNode, ConnectionLine,
 * and CosmicMap itself all import from one source of truth.
 *
 * v3 Changes — Zoom-Reveal Disambiguation:
 *   - Added MIN_SCREEN_GAP: minimum screen pixels between node centers.
 *   - Added hitRadiusForZoom(): returns SVG-space hit radius that stays
 *     constant in screen pixels regardless of zoom. At zoom 1.0 the hit
 *     radius matches the old NODE_HIT_AREA; at zoom 2.0 the SVG-space
 *     hit radius is halved (so it occupies the same screen pixels).
 *   - Added ringInflationForZoom(): returns a multiplier ≥ 1.0 that
 *     inflates ring radii as the user zooms in past zoom 1.0. This is
 *     what makes nodes physically spread apart when you lean in.
 *
 * The two work together:
 *   • ringInflation pushes nodes farther apart in layout (SVG) space.
 *   • hitRadiusForZoom keeps the clickable area screen-proportional.
 * Net effect: overlapping nodes at overview zoom become individually
 * clickable the moment the user zooms in.
 *
 * Place at: src/components/map/mapConstants.jsx
 */

// ── Node sizing ──────────────────────────────────────────────────────────

export const NODE_SIZE = 52;
export const NODE_SIZE_MAX = 88;              // Max expanded size on proximity bloom
export const NODE_HIT_AREA = 60;
export const MOBILE_NODE_HIT_AREA = 84;       // Larger tap target on touch devices (≥44px at zoom 0.55)
export const CHALLENGE_SIZE = 44;
export const CHALLENGE_SIZE_MAX = 64;
export const CHALLENGE_HIT_AREA = 56;         // Hit area for challenge diamonds (desktop)
export const MOBILE_CHALLENGE_HIT_AREA = 80;  // Hit area for challenge diamonds (mobile)
export const CLICK_THRESHOLD = 5;
export const MOBILE_CLICK_THRESHOLD = 12;     // Fat-finger tolerance on touch
export const CHALLENGE_HUB_RADIUS = 95;
export const LINE_HIT_AREA = 20;
export const PLUCK_DURATION = 800;
export const RIPPLE_COUNT = 3;
export const CLUSTER_ZOOM_THRESHOLD = 0.45;

// ── Zoom-reveal disambiguation ───────────────────────────────────────────
// The minimum gap (in screen pixels) we want between two adjacent node
// centers before they feel "crowded." Used conceptually by callers to
// decide how aggressive ring inflation should be. Not directly consumed
// by the math below, but documents intent for future tuning.
export const MIN_SCREEN_GAP = 56;

// Below this zoom level, layout stays compact (constellation-at-rest look).
// At or above this zoom, ring inflation begins pushing nodes apart so
// crowded clusters physically spread out as the user leans in.
export const RING_INFLATION_START_ZOOM = 1.0;

// The inflation curve reaches full effect (3× spacing) at this zoom level.
// Between START and FULL, inflation ramps smoothly.
export const RING_INFLATION_FULL_ZOOM = 2.5;

// Max multiplier applied to ring radii at peak zoom. Chosen so that a
// ring-0 cluster (6 nodes, 40px radius) at zoom 2.5 has centers ~175px
// apart in layout space — which is >100px of screen gap at that zoom.
export const RING_INFLATION_MAX = 3.0;

// ── Project Group / Cluster sizing ───────────────────────────────────────
// Satellites (sub-assets of a grouped project) orbit closer to their root
// and render smaller so the cluster reads as one visual unit.

export const CLUSTER_SATELLITE_SIZE = 34;     // Smaller node for grouped assets
export const CLUSTER_SATELLITE_MAX = 52;      // Max bloom size for satellites
export const CLUSTER_ORBIT_RADIUS = 42;       // Distance from root center to satellite center
export const CLUSTER_ORBIT_RADIUS_DENSE = 56; // Orbit expands when > 4 satellites
export const CLUSTER_LINE_WIDTH = 1;          // Thin dashed line connecting satellite → root
export const CLUSTER_LINE_DASH = '3,4';       // Dash pattern for cluster lines

// ── Bloom ────────────────────────────────────────────────────────────────

export const BLOOM_RADIUS = 160;
export const BLOOM_INNER = 40;

export const BLOOM_EASE = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
export const BLOOM_DURATION = '0.22s';

// ── Mobile bloom ─────────────────────────────────────────────────────────

export const MOBILE_BLOOM_HOLD_MS = 350;
export const MOBILE_BLOOM_RADIUS = 200;
export const MOBILE_LONG_PRESS_MS = 450;      // Long-press duration to open project detail on mobile

// ── Utility functions ────────────────────────────────────────────────────

/**
 * Calculate bloom scale for a node based on cursor proximity.
 * Returns 0 (no bloom) to 1 (max bloom).
 */
export function getBloomFactor(nodeX, nodeY, cursorX, cursorY, radius = BLOOM_RADIUS, inner = BLOOM_INNER) {
  const dx = nodeX - cursorX;
  const dy = nodeY - cursorY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist >= radius) return 0;
  if (dist <= inner) return 1;
  // Smooth ease-out curve — nodes close to cursor grow faster
  const t = 1 - (dist - inner) / (radius - inner);
  return t * t * (3 - 2 * t); // smoothstep
}

export function lerp(a, b, t) {
  return a + (b - a) * t;
}

/**
 * Smoothstep interpolation between edge0 and edge1.
 * Returns 0 below edge0, 1 above edge1, smooth S-curve between.
 */
export function smoothstep(edge0, edge1, x) {
  if (edge1 <= edge0) return x < edge0 ? 0 : 1;
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * Hit-radius in SVG layout space that remains constant in screen pixels.
 *
 * The entire SVG scales by `zoom`, so an SVG-space circle of radius R
 * appears as R * zoom screen pixels. To keep the clickable target at a
 * constant *screen* size, we divide the base screen-size hit radius by
 * the current zoom level.
 *
 * @param {number} baseHitArea - full-diameter screen-size hit area in px
 *                               (e.g. NODE_HIT_AREA or MOBILE_NODE_HIT_AREA)
 * @param {number} zoom        - current map zoom (1.0 = default)
 * @returns {number} the SVG-space radius to use for the invisible hit circle
 */
export function hitRadiusForZoom(baseHitArea, zoom) {
  // baseHitArea is a diameter; we return a radius.
  // Clamp zoom so the hit area never explodes to infinity at very low zoom
  // (at 0.25× zoom, unclamped hit radius would be 4× normal, which is fine,
  //  but we cap it to avoid absurd values).
  const z = Math.max(0.35, Math.min(4, zoom || 1));
  return (baseHitArea / 2) / z;
}

/**
 * Ring-radius inflation multiplier as a function of zoom.
 *
 * Returns 1.0 at or below RING_INFLATION_START_ZOOM (no change).
 * Ramps smoothly to RING_INFLATION_MAX at RING_INFLATION_FULL_ZOOM.
 * Stays at RING_INFLATION_MAX above that.
 *
 * The layout useMemo in CosmicMap multiplies INNER_RADIUS and MIN_SPACING
 * by this value, so zooming in physically spreads nodes apart in SVG
 * coordinates (not just visually, but in the underlying math).
 *
 * @param {number} zoom - current map zoom (1.0 = default)
 * @returns {number} multiplier >= 1.0
 */
export function ringInflationForZoom(zoom) {
  const z = zoom || 1;
  const t = smoothstep(RING_INFLATION_START_ZOOM, RING_INFLATION_FULL_ZOOM, z);
  return 1 + (RING_INFLATION_MAX - 1) * t;
}
